import type { ReactNode } from 'react';
import { Surface } from '../ui/Surface';

// ------------------------------------------------------------------
// GallerySection — story 2-147p (P10 §4, DOD). Formalizes the gallery's own
// per-primitive demo wrapper — previously a page-local, unexported
// `Section()` function declared inside `DesignSystemPage.tsx` itself
// (never imported anywhere else) — into a real, reusable component that
// `2-147qa` onward imports directly instead of copy-pasting the local
// helper into every new gallery-adjacent file.
//
// `data-testid="gallery-section"` and the <h2> title are LOAD-BEARING —
// keep both exactly as-is:
//   - `e2e/specs/story-2-134-visual-baseline.spec.mjs` locates every
//     Section via `page.getByTestId('gallery-section')` and reads its <h2>
//     for the screenshot's own filename.
//   - `e2e/specs/story-2-43.spec.mjs` ("check B") asserts at least one
//     `gallery-section` renders with a visible <h2>.
// Renaming either hook without updating both specs breaks them silently.
//
// This story's own worked-proof Section (DesignSystemPage.tsx's "Gallery
// scaffolding" Section) is the FIRST real consumer. The page's existing 57
// `Section()` call sites are deliberately NOT migrated onto this component
// here — that migration is `2-147vb`'s explicit job, out of this story's
// scope ("Not in scope: migrating existing /app/design sections onto this
// scaffolding yet").
// ------------------------------------------------------------------

export interface GallerySectionProps {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}

export function GallerySection({ title, hint, children }: GallerySectionProps): JSX.Element {
  // Story 2-147ta: composes `Surface` (flat, `xl` padding — the same p-6
  // this section always rendered) instead of re-deriving the border/bg/
  // radius by hand, dogfooding the new primitive in the gallery's own
  // scaffolding. `as="section"` keeps the exact prior element/testid/heading
  // contract `story-2-134`/`story-2-43` both key off — see this file's own
  // header comment for why both are load-bearing.
  return (
    <Surface as="section" variant="flat" padding="xl" data-testid="gallery-section">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
      <div className="mt-4">{children}</div>
    </Surface>
  );
}
